/**
 * ResetPasswordPage — public page an emailed password reset link opens (ORCH-296).
 *
 * Purpose: reads `token` from the query string, collects the new password with the shared
 *          NewPasswordFields (ORCH-396 policy), posts to POST /auth/password-reset/confirm,
 *          then sends the person to sign in. Every session they had has ended.
 */

import React, { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import userManagementService from '../../services/userManagementService';
import NewPasswordFields, { newPasswordError } from '../../components/auth/NewPasswordFields';

const INVALID_LINK =
  'This link has expired or was already used. Ask your administrator for a new one.';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const problem = newPasswordError(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }
    setIsSubmitting(true);
    try {
      const { email } = await userManagementService.confirmPasswordReset({ token, password });
      navigate('/login', { replace: true, state: { passwordReset: true, email } });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail as { code?: string; message?: string } | undefined;
      if (detail?.code === 'RESET_TOKEN_INVALID') {
        setError(INVALID_LINK);
      } else {
        setError(detail?.message || 'Could not change your password. Try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [confirmPassword, navigate, password, token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }} elevation={3}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Reset your password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a new password for your OrchestrateHQ account. You&apos;ll be signed out
            everywhere and can then sign in with it.
          </Typography>

          {!token ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This link is missing a token. Open the link from your password reset email, or ask
              your administrator to send a new one.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <NewPasswordFields
              idPrefix="reset-password"
              passwordLabel="New password"
              password={password}
              confirmPassword={confirmPassword}
              onPasswordChange={setPassword}
              onConfirmPasswordChange={setConfirmPassword}
              disabled={!token || isSubmitting}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 1 }}
            disabled={!token || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Saving…' : 'Set new password'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to sign in
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
